import { IconClockHour5 } from '@tabler/icons-react'

import { AstraButton, AstraIcon, AstraTooltip, day, diff, getOnlyDate, humanizeDate, StickerNoteIcon } from '~/astra'
import { useLocationNotes } from '~/hooks/useLocationNotes'
import {
    PractitionerScheduleLocation,
    PractitionerScheduleStatus,
    usePractitionerSchedule,
    usePractitionerScheduleLocations,
    usePractitionerScheduleStatuses,
} from '~/storm'

import { DateLocationCell, DatePractitionerCell } from '.'

type NoteProps = {
    openNote: () => void
    isSingleCell: boolean
    hasNote: boolean
}

const Note = ({ openNote, isSingleCell, hasNote }: NoteProps) => {
    const text = isSingleCell ? (hasNote ? 'Endre notat' : 'Legg til notat') : 'Du kan ikke skrive kommentar på flere dager samtidig. Velg kun én dag.'
    return (
        <AstraTooltip tooltipContent={text}>
            <AstraButton variant="ghost" size="medium" onClick={openNote} disabled={!isSingleCell} icon={<StickerNoteIcon className="size-6" />} />
        </AstraTooltip>
    )
}

type ActivityLogProps = {
    openActivityLog: () => void
    isSingleCell: boolean
}

const ActivityLog = ({ openActivityLog, isSingleCell }: ActivityLogProps) => {
    const text = isSingleCell ? 'Vis aktivitetslogg' : 'Du kan ikke se aktivitetslogg for flere dager samtidig. Velg kun én dag.'
    return (
        <AstraTooltip tooltipContent={text}>
            <AstraButton
                variant="ghost"
                size="medium"
                onClick={openActivityLog}
                disabled={!isSingleCell}
                icon={<AstraIcon icon={IconClockHour5} size="large" />}
            />
        </AstraTooltip>
    )
}

function getLatestUpdatedAtAndBy(schedules: (PractitionerScheduleStatus | PractitionerScheduleLocation)[]) {
    return schedules
        .map(({ updated_at, updated_by }) => ({ updated_at, updated_by }))
        .toSorted((a, b) => diff(day(b.updated_at), day(a.updated_at)))
        .at(0)
}

type PractitionerProps = {
    selectedCells: [DatePractitionerCell, ...DatePractitionerCell[]] // array with at least one element
    openNote: () => void
    openActivityLog: () => void
}

export const PractitionerBottomBar = ({ selectedCells, openNote, openActivityLog }: PractitionerProps) => {
    const firstCell = selectedCells[0]
    const singleCell = selectedCells.length === 1

    const allScheduleStatusesByCell = usePractitionerScheduleStatuses({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const allScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const latestUpdatedAtAndBy = getLatestUpdatedAtAndBy([...allScheduleStatusesByCell, ...allScheduleLocationsByCell])

    const firstCellSchedule = usePractitionerSchedule({ where: { scheduled_date: getOnlyDate(firstCell.date), practitioner_id: firstCell.practitionerId } })
    const selectedCellNote = singleCell ? firstCellSchedule?.comment : null

    return (
        <div className="pointer-events-auto flex items-center justify-between px-3 py-1">
            <div className="flex justify-start gap-2">
                <Note openNote={openNote} isSingleCell={singleCell} hasNote={!!selectedCellNote} />
                <ActivityLog openActivityLog={openActivityLog} isSingleCell={singleCell} />
            </div>
            <div>
                {latestUpdatedAtAndBy && (
                    <AstraTooltip tooltipContent={`Sist endret av ${latestUpdatedAtAndBy.updated_by} ${humanizeDate(day(latestUpdatedAtAndBy.updated_at))}`}>
                        <p className="cursor-pointer text-xs text-gray-500 hover:underline">{humanizeDate(day(latestUpdatedAtAndBy.updated_at))}</p>
                    </AstraTooltip>
                )}
            </div>
        </div>
    )
}

type LocationProps = {
    selectedCells: [DateLocationCell, ...DateLocationCell[]] // array with at least one element
    openNote: () => void
    openActivityLog: () => void
}

export const LocationBottomBar = ({ selectedCells, openNote, openActivityLog }: LocationProps) => {
    const selectedCell = selectedCells[0]
    const { note } = useLocationNotes({ location: selectedCell })

    const allScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, locationId }) => ({ scheduled_date: getOnlyDate(date), location_id: locationId })),
        },
    })
    const latestUpdatedAtAndBy = getLatestUpdatedAtAndBy(allScheduleLocationsByCell)

    const singleCell = selectedCells.length === 1

    return (
        <div className="pointer-events-auto flex items-center justify-between px-3 py-1">
            <div className="flex justify-start gap-2">
                <Note openNote={openNote} isSingleCell={singleCell} hasNote={!!note} />
                <ActivityLog openActivityLog={openActivityLog} isSingleCell={singleCell} />
            </div>
            <div>
                {latestUpdatedAtAndBy && (
                    <AstraTooltip tooltipContent={`Sist endret av ${latestUpdatedAtAndBy.updated_by} ${humanizeDate(day(latestUpdatedAtAndBy.updated_at))}`}>
                        <p className="cursor-pointer text-xs text-gray-500 hover:underline">{humanizeDate(day(latestUpdatedAtAndBy.updated_at))}</p>
                    </AstraTooltip>
                )}
            </div>
        </div>
    )
}
